import { create } from 'zustand';
import { leadService } from '../services/leadService';
import {
  Lead,
  CreateLeadRequest,
  SearchLeadsRequest,
  LeadSourceOption,
  LeadStatus,
  ImportLeadsResponse,
  BulkCreateTaskResponse,
} from '../types/lead';
import { ApiError } from '../types/api';
import { formatApiError } from '../utils/errorHandler';
import { useToastStore } from './toastStore';

interface LeadState {
  leads: Lead[];
  leadSources: LeadSourceOption[];
  currentPage: number;
  pageSize: number;
  total: number;
  totalPages: number;
  searchQuery: string;
  statusFilter: string;
  genderFilter: string;
  leadTypeFilter: string;
  isLoading: boolean;
  importingLeads: boolean;
  exportingLeads: boolean;
  creatingTasks: boolean;
  deletingLeadId: string | null;
  error: ApiError | null;

  fetchLeadSources: () => Promise<void>;
  fetchLeads: () => Promise<void>;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setStatusFilter: (value: string) => void;
  setGenderFilter: (value: string) => void;
  setLeadTypeFilter: (value: string) => void;
  setSearchQuery: (value: string) => void;
  clearFilters: () => void;
  createLead: (data: CreateLeadRequest) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  bulkDelete: (ids: string[]) => Promise<void>;
  bulkCreateTasks: (
    ids: string[],
    assigneeUserId?: number
  ) => Promise<BulkCreateTaskResponse>;
  importLeads: (file: File) => Promise<ImportLeadsResponse>;
  exportLeads: (leadIds?: string[]) => Promise<void>;
  clearError: () => void;
}

const toastLeadError = (title: string, error: unknown, fallback: string) => {
  useToastStore.getState().showErrorMessage({
    title,
    message: formatApiError(error).trim() || fallback,
  });
};

const buildSearchParams = (state: LeadState): SearchLeadsRequest => ({
  page: state.currentPage,
  limit: state.pageSize,
  tab: 'all',
  search: state.searchQuery.trim() || undefined,
  status:
    state.statusFilter !== 'all' ? (state.statusFilter as LeadStatus) : undefined,
  gender:
    state.genderFilter !== 'all'
      ? (state.genderFilter as 'Male' | 'Female')
      : undefined,
  lead_type:
    state.leadTypeFilter !== 'all' ? Number(state.leadTypeFilter) : undefined,
});

export const useLeadStore = create<LeadState>()((set, get) => {
  const refreshLeads = () => {
    void get().fetchLeads().catch((error) => {
      toastLeadError('Failed to load leads', error, 'Unable to load leads.');
    });
  };

  return {
  leads: [],
  leadSources: [],
  currentPage: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
  searchQuery: '',
  statusFilter: 'all',
  genderFilter: 'all',
  leadTypeFilter: 'all',
  isLoading: false,
  importingLeads: false,
  exportingLeads: false,
  creatingTasks: false,
  deletingLeadId: null,
  error: null,

  fetchLeadSources: async () => {
    try {
      const sources = await leadService.getLeadSources();
      set({ leadSources: sources });
    } catch (error) {
      // Keep empty so filters can fall back to static options, but surface the API message.
      toastLeadError('Failed to load lead sources', error, 'Unable to load lead sources.');
    }
  },

  fetchLeads: async () => {
    const state = get();
    set({ isLoading: true, error: null });
    try {
      const response = await leadService.searchLeads(buildSearchParams(state));
      set({
        leads: response.data,
        currentPage: response.metaData.page,
        pageSize: response.metaData.limit,
        total: response.metaData.total,
        totalPages: response.metaData.totalPages,
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  setPage: (page: number) => {
    set({ currentPage: page });
    refreshLeads();
  },

  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 1 });
    refreshLeads();
  },

  setStatusFilter: (value: string) => {
    set({ statusFilter: value, currentPage: 1 });
    refreshLeads();
  },

  setGenderFilter: (value: string) => {
    set({ genderFilter: value, currentPage: 1 });
    refreshLeads();
  },

  setLeadTypeFilter: (value: string) => {
    set({ leadTypeFilter: value, currentPage: 1 });
    refreshLeads();
  },

  setSearchQuery: (value: string) => {
    set({ searchQuery: value, currentPage: 1 });
    refreshLeads();
  },

  clearFilters: () => {
    set({
      searchQuery: '',
      statusFilter: 'all',
      genderFilter: 'all',
      leadTypeFilter: 'all',
      currentPage: 1,
    });
    refreshLeads();
  },

  createLead: async (data: CreateLeadRequest) => {
    set({ isLoading: true, error: null });
    try {
      await leadService.createLead(data);
      await get().fetchLeads();
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  deleteLead: async (id: string) => {
    set({ deletingLeadId: id, error: null });
    try {
      await leadService.deleteLead(id);
      await get().fetchLeads();
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ deletingLeadId: null });
    }
  },

  bulkDelete: async (ids: string[]) => {
    set({ error: null });
    try {
      await leadService.bulkDelete(ids.map(Number));
      await get().fetchLeads();
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    }
  },

  bulkCreateTasks: async (ids: string[], assigneeUserId?: number) => {
    set({ creatingTasks: true, error: null });
    try {
      return await leadService.bulkCreateTask(ids.map(Number), undefined, assigneeUserId);
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ creatingTasks: false });
    }
  },

  importLeads: async (file: File) => {
    set({ importingLeads: true, error: null });
    try {
      const result = await leadService.importLeads(file);
      await get().fetchLeads();
      return result;
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ importingLeads: false });
    }
  },

  exportLeads: async (leadIds?: string[]) => {
    set({ exportingLeads: true, error: null });
    try {
      const state = get();
      const body =
        leadIds && leadIds.length > 0
          ? { leadIds: leadIds.map(Number) }
          : { filters: buildSearchParams(state) };
      await leadService.exportAndDownload(body);
    } catch (error) {
      set({ error: error as ApiError });
      throw error;
    } finally {
      set({ exportingLeads: false });
    }
  },

  clearError: () => set({ error: null }),
};
});
